import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  useQueryParams,
  NumberParam,
  StringParam,
  withDefault,
} from 'use-query-params';
import { list, typesCount } from '../../lib/fetch/profiles';
import ProfilesPresentational from './ProfilesPresentational';
import { useLoading } from '../../commons/Loading';
import { useAuth } from '../../commons/Auth';
import axios from 'axios';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

function Profiles(props) {
  const [{ newToken } = {}] = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [t] = useTranslation('CONTACTS');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [managers, setManagers] = useState([]);
  const [softwares, setSoftwares] = useState([]);

  const { history } = props;
  const [pathname, setPathname] = useState('');

  const [query, setQuery] = useQueryParams({
    type: withDefault(StringParam),
    profileStatus: withDefault(StringParam),
    affiliationStatus: withDefault(StringParam),
    braceletStatus: withDefault(StringParam),
    crmStatus: withDefault(StringParam),
    softwareStatus: withDefault(StringParam),
    search: withDefault(StringParam),
    offset: NumberParam,
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    cancellationDateYear: NumberParam,
    cancellationDateMonth: NumberParam,
  });

  const {
    search,
    offset,
    limit,
    type,
    profileStatus,
    affiliationStatus,
    braceletStatus,
    crmStatus,
    softwareStatus,
    cancellationDateYear,
    cancellationDateMonth,
  } = query;

  const [profileTypesCount, setProfileTypesCount] = useState([]);

  const profileTypes = async () => {
    showLoading();

    const { error, data } = await typesCount({
      newToken,
      pathname,
      forProfilesInfo: true,
    });

    if (error) {
      hideLoading();
      setProfileTypesCount({});
      setTotal(0);
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    setProfileTypesCount(
      data.filter((elem) => {
        if (elem.type !== 'ADMIN_COLLAB') return elem;
      })
    );
    hideLoading();
  };

  useEffect(() => {
    const fetchProfiles = async () => {
      showLoading();

      const { error, data, headers } = await list({
        newToken,
        search,
        offset,
        limit,
        type,
        profileStatus,
        affiliationStatus,
        braceletStatus,
        crmStatus,
        softwareStatus,
        cancellationDateYear,
        cancellationDateMonth,
        pathname,
        forProfilesInfo: true,
      });

      if (error) {
        hideLoading();
        setData([]);
        setTotal(0);
        alert.error(t('COMMON:GENERIC_ERROR'));
        return;
      }

      setData(data);
      setTotal(Number(headers['x-total-count']));
      hideLoading();
    };

    if (pathname !== '') {
      fetchProfiles();
      profileTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    offset,
    limit,
    type,
    profileStatus,
    affiliationStatus,
    braceletStatus,
    crmStatus,
    softwareStatus,
    cancellationDateYear,
    cancellationDateMonth,
    pathname,
  ]);

  useEffect(() => {
    if (history?.location?.pathname) {
      setPathname(history.location.pathname.replace(/^\//, ''));
    }
  }, [history]);

  function onFilter(query) {
    if (
      (query.search && query.search !== search) || // first search
      (search && query.search === '') || // search deleted
      (query.type && query.type !== type) ||
      (type && query.type !== type) ||
      (query.profileStatus && query.profileStatus !== profileStatus) ||
      (profileStatus && query.profileStatus !== profileStatus) ||
      (query.affiliationStatus &&
        query.affiliationStatus !== affiliationStatus) ||
      (affiliationStatus && query.affiliationStatus !== affiliationStatus) ||
      (query.braceletStatus && query.braceletStatus !== braceletStatus) ||
      (braceletStatus && query.braceletStatus !== braceletStatus) ||
      (query.crmStatus && query.crmStatus !== crmStatus) ||
      (crmStatus && query.crmStatus !== crmStatus) ||
      (query.softwareStatus && query.softwareStatus !== softwareStatus) ||
      (softwareStatus && query.softwareStatus !== softwareStatus) ||
      (query.cancellationDateYear &&
        query.cancellationDateYear !== cancellationDateYear) ||
      (cancellationDateYear &&
        query.cancellationDateYear !== cancellationDateYear) ||
      (query.cancellationDateMonth &&
        query.cancellationDateMonth !== cancellationDateMonth) ||
      (cancellationDateMonth &&
        query.cancellationDateMonth !== cancellationDateMonth) ||
      (query.limit && query.limit !== limit)
    ) {
      query.offset = 0; // return to first page
    }
    setQuery(query);
  }

  const fetchManagers = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/managers`,
      {
        headers: { Authorization: newToken },
        // read-only view: include also soft-deleted managers
        params: { all: true },
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    let { data = [], headers = {} } = response;
    setManagers(data);
  };

  const fetchSoftwares = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/softwares`,
      {
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    let { data = [], headers = {} } = response;
    setSoftwares(data);
  };

  useEffect(() => {
    fetchManagers();
    fetchSoftwares();
  }, []);

  return (
    <ProfilesPresentational
      data={data}
      total={total}
      query={query}
      profileTypesCount={profileTypesCount}
      onFilter={onFilter}
      managers={managers}
      softwares={softwares}
      pathname={pathname}
      {...props}
    />
  );
}

export default Profiles;
